export interface ConversationChatMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    toolCalls: Record<string, unknown>[] | null;
    createdAt: string;
    scheduleName: string | null;
    schedule: { id: string } | null;
    attachments: {
        id: string;
        source: string;
        fileName: string;
        contentType: string;
        downloadUrl: string;
    }[];
}

export interface ChatMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    status: 'COMPLETED' | 'CANCELLED';
    tool_calls: Record<string, unknown>[] | null;
    created_at: string;
}

export interface ConversationStreamCallbacks {
    onTurnStarted?: (data: { conversation_id: string; user_message_id: string }) => void;
    onFileSearch?: (data: { queries: string[]; result_count: number }) => void;
    onToolCallStarted?: (data: { index: number; tool_name: string; tool_input: Record<string, unknown> }) => void;
    onToolCallFinished?: (data: { index: number; tool_name: string; tool_output: string }) => void;
    onThinking?: (data: { text: string }) => void;
    onDone?: (data: {
        message: ChatMessage;
        total_tool_calls: number;
        truncated?: boolean;
        conversation?: { id: string; title: string; context_tokens: number };
    }) => void;
    onCancelled?: (data: {
        message: ChatMessage | null;
        total_tool_calls: number;
        conversation?: { id: string; title: string; context_tokens: number };
    }) => void;
}

const backendOrigin = (process.env.GRAPHQL_URL ?? '').replace(/graphql\/?$/, '');

export const streamConversationMessage = async (
    conversationId: string,
    message: string,
    attachmentIds: string[] | null,
    editMessageId: string | null,
    callbacks: ConversationStreamCallbacks
): Promise<void> => {
    const body: { message: string; attachment_ids?: string[]; edit_message_id?: string } = { message };
    if (attachmentIds?.length) body.attachment_ids = attachmentIds;
    if (editMessageId) body.edit_message_id = editMessageId;

    const response = await fetch(`${backendOrigin}agent-conversations/${conversationId}/chat/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });

    if (!response.ok || !response.body) {
        const errorBody = await response.json().catch(() => null);
        const error: Error & { code?: string } = new Error(
            errorBody?.detail || `Failed to send message: ${response.statusText}`
        );
        error.code = errorBody?.code;
        throw error;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let terminalEventSeen = false;

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer = buffer + decoder.decode(value, { stream: true });

        let separatorIndex;
        while ((separatorIndex = buffer.indexOf('\n\n')) !== -1) {
            const frame = buffer.slice(0, separatorIndex);
            buffer = buffer.slice(separatorIndex + 2);

            if (!frame || frame.startsWith(':')) continue; // skipping empty frames and heartbeat pings

            const eventLine = frame.split('\n').find((line) => line.startsWith('event: '));
            const dataLine = frame.split('\n').find((line) => line.startsWith('data: '));

            if (!eventLine || !dataLine) continue;

            const event = eventLine.slice('event: '.length);
            const data = JSON.parse(dataLine.slice('data: '.length));

            switch (event) {
                case 'turn_started':
                    callbacks.onTurnStarted?.(data);
                    break;

                case 'file_search':
                    callbacks.onFileSearch?.(data);
                    break;
                case 'tool_call_started':
                    callbacks.onToolCallStarted?.(data);
                    break;
                case 'tool_call_finished':
                    callbacks.onToolCallFinished?.(data);
                    break;
                case 'thinking':
                    callbacks.onThinking?.(data);
                    break;
                case 'done':
                    terminalEventSeen = true;
                    callbacks.onDone?.(data);
                    break;
                case 'cancelled':
                    terminalEventSeen = true;
                    callbacks.onCancelled?.(data);
                    break;
                case 'error': {
                    const streamError: Error & { code?: string } = new Error(data.detail || 'Chat turn failed.');
                    streamError.code = data.reason;
                    throw streamError;
                }
            }
        }
    }

    // a stream that ends without done/cancelled/error died mid-turn (e.g. a deploy, pod restart or
    // gateway cut); the backend keeps running and usually persists the turn, so this gets its own
    // code instead of the generic send failure
    if (!terminalEventSeen) {
        const connectionError: Error & { code?: string } = new Error('Stream ended without a terminal event');
        connectionError.code = 'CONNECTION_LOST';
        throw connectionError;
    }
};

export const cancelChatStream = async (conversationId: string): Promise<void> => {
    const response = await fetch(`${backendOrigin}agent-conversations/${conversationId}/chat/cancel`, {
        method: 'POST',
        credentials: 'include',
    });

    if (!response.ok) {
        throw new Error(`Failed to cancel chat: ${response.statusText}`);
    }
};
